import React, { useState, useEffect, useMemo } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../firebase";
import { handleDeleteFine } from "../utils/deleteFineHandler";
import Select from "react-select";
import "../styles/paidUnpaidDeleteButton.css";

const PaidUnpaidManager = ({ show, setShow, fines, users, fetchData }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [paymentMode, setPaymentMode] = useState("all");
  const [selectedFines, setSelectedFines] = useState([]);
  const [userFines, setUserFines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const resetModal = () => {
    setSelectedUser(null);
    setSelectedStatus(null);
    setPaymentMode("all");
    setSelectedFines([]);
    setUserFines([]);
    setSelectAll(false);
  };

  // Load fines of selected user
  useEffect(() => {
    if (!selectedUser) {
      setUserFines([]);
      return;
    }
    const filtered = fines.filter((f) => f.userId === selectedUser.value);
    setUserFines(filtered);
    setSelectedFines([]);
    setSelectAll(false);
  }, [selectedUser, fines]);

  const filteredFines = useMemo(
    () =>
      userFines.filter(
        (f) => !selectedStatus || f.status === selectedStatus.value,
      ),
    [userFines, selectedStatus],
  );

  // Summary totals
  const totalPaid = useMemo(
    () =>
      filteredFines
        .filter((f) => f.status === "Paid")
        .reduce((acc, f) => acc + Number(f.amount), 0),
    [filteredFines],
  );
  const totalPending = useMemo(
    () =>
      filteredFines
        .filter((f) => f.status === "Pending")
        .reduce((acc, f) => acc + Number(f.amount), 0),
    [filteredFines],
  );
  const totalSelected = useMemo(
    () =>
      filteredFines
        .filter((f) => selectedFines.includes(f.fineId))
        .reduce((acc, f) => acc + Number(f.amount), 0),
    [filteredFines, selectedFines],
  );

  // Auto select all sync
  useEffect(() => {
    const selectable = filteredFines.filter((f) => f.status === "Pending");
    setSelectAll(selectedFines.length === selectable.length);
  }, [selectedFines, filteredFines]);

  const toggleFineSelection = (fineId) => {
    setSelectedFines((prev) => {
      const set = new Set(prev);
      set.has(fineId) ? set.delete(fineId) : set.add(fineId);
      return [...set];
    });
  };

  const handleSelectAll = () => {
    const pendingIds = filteredFines
      .filter((f) => f.status === "Pending")
      .map((f) => f.fineId);
    setSelectedFines(selectAll ? [] : pendingIds);
  };

  const updateSingleFine = async (userId, fineId, status) => {
    const fineRef = doc(db, "Fines", userId, "fines", fineId);
    const fineDoc = await getDoc(fineRef);
    if (!fineDoc.exists()) throw new Error("Fine not found");
    const fineAmount = Number(fineDoc.data().amount) || 0;
    await updateDoc(fineRef, {
      status,
      paidOn: status === "Paid" ? serverTimestamp() : null,
    });
    const userRef = doc(db, "Fines", userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const currentPending = userDoc.data().PendingFine || 0;
      const updatedPending =
        status === "Paid"
          ? Math.max(0, currentPending - fineAmount)
          : currentPending + fineAmount;
      await updateDoc(userRef, {
        PendingFine: updatedPending,
        lastUpdated: serverTimestamp(),
      });
    }
  };

  const markFineStatus = async (userId, fineId, newStatus) => {
    const confirm = await Swal.fire({
      title: "Confirm",
      text: `Mark fine as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
    });
    if (!confirm.isConfirmed) return;
    Swal.fire({ title: "Updating...", didOpen: () => Swal.showLoading() });
    try {
      await updateSingleFine(userId, fineId, newStatus);
      Swal.fire("Success", "Fine updated!", "success");
      fetchData();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const formatDate = (date) => {
    if (!date) return "";

    const d = date?.toDate ? date.toDate() : new Date(date);

    const day = String(d.getDate()).padStart(2, "0");

    const month = d.toLocaleString("en-US", { month: "long" });

    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const handleSavePayment = async () => {
    const finesToUpdate =
      paymentMode === "all"
        ? filteredFines.filter((f) => f.status === "Pending")
        : filteredFines.filter((f) => selectedFines.includes(f.fineId));
    if (!finesToUpdate.length) {
      Swal.fire("No fines selected", "", "warning");
      return;
    }
    const confirm = await Swal.fire({
      title: "Confirm Payment",
      text: `Mark ${finesToUpdate.length} fines as paid? Total Rs ${
        paymentMode === "all" ? totalPending : totalSelected
      }`,
      icon: "question",
      showCancelButton: true,
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);
      Swal.fire({ title: "Processing...", didOpen: () => Swal.showLoading() });
      const batch = writeBatch(db);
      const userPendingMap = {};
      for (const fine of finesToUpdate) {
        const fineRef = doc(db, "Fines", fine.userId, "fines", fine.fineId);
        batch.update(fineRef, { status: "Paid", paidOn: serverTimestamp() });
        userPendingMap[fine.userId] =
          (userPendingMap[fine.userId] || 0) + Number(fine.amount);
      }
      for (const userId in userPendingMap) {
        const userRef = doc(db, "Fines", userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const currentPending = userDoc.data().PendingFine || 0;
          batch.update(userRef, {
            PendingFine: Math.max(0, currentPending - userPendingMap[userId]),
            lastUpdated: serverTimestamp(),
          });
        }
      }
      await batch.commit();
      Swal.fire("Success", "All fines updated!", "success");
      fetchData();
      resetModal();
      setShow(false);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const userOptions = users.map((u) => ({ value: u.id, label: u.userName }));
  const statusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Pending", label: "Pending" },
  ];

  return (
    <div
      className="modal d-block"
      style={{ background: "rgba(0,0,0,0.3)", backdropFilter: "blur(4px)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Paid / Unpaid Manager</h5>
            <button
              className="btn-close"
              onClick={() => {
                resetModal();
                setShow(false);
              }}
            />
          </div>

          <div className="modal-body">
            {/* Searchable Dropdowns */}
            <div className="row mb-3">
              <div className="col-md-6 mb-2">
                <label className="fw-semibold">User</label>
                <Select
                  options={userOptions}
                  value={selectedUser}
                  onChange={setSelectedUser}
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "8px",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                      borderColor: "#ccc",
                    }),
                    singleValue: (base) => ({ ...base, color: "#333" }),
                    placeholder: (base) => ({ ...base, color: "#999" }),
                  }}
                />
              </div>
              <div className="col-md-6 mb-2">
                <label className="fw-semibold">Status Filter</label>
                <Select
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "8px",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                      borderColor: "#ccc",
                    }),
                    singleValue: (base) => ({ ...base, color: "#333" }),
                    placeholder: (base) => ({ ...base, color: "#999" }),
                  }}
                />
              </div>
            </div>

            {/* Summary Cards */}
            {selectedUser && (
              <div className="d-flex gap-3 mb-3">
                <div className="summary-card paid">
                  <span>✔ Paid</span>
                  <strong>Rs {totalPaid}</strong>
                </div>
                <div className="summary-card pending">
                  <span>⏳ Pending</span>
                  <strong>Rs {totalPending}</strong>
                </div>
                {paymentMode === "selected" && (
                  <div className="summary-card selected">
                    <span>💳 Selected</span>
                    <strong>Rs {totalSelected}</strong>
                  </div>
                )}
              </div>
            )}

            {/* Payment Mode */}
            {selectedUser && totalPending > 0 && (
              <div className="mb-3">
                <label className="fw-semibold">Payment Mode</label>
                <div>
                  <input
                    type="radio"
                    checked={paymentMode === "all"}
                    onChange={() => setPaymentMode("all")}
                  />{" "}
                  Mark ALL Pending
                  <br />
                  <input
                    type="radio"
                    checked={paymentMode === "selected"}
                    onChange={() => setPaymentMode("selected")}
                  />{" "}
                  Mark Selected Only
                </div>
              </div>
            )}

            {/* Table */}
            {selectedUser && (
              <div className="table-responsive" style={{ maxHeight: 300 }}>
                <table className="table table-sm table-bordered">
                  <thead className="table-light">
                    <tr>
                      {paymentMode === "selected" && (
                        <th>
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                          />
                        </th>
                      )}
                      <th>Type</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFines.length ? (
                      filteredFines.map((f) => (
                        <tr key={f.fineId}>
                          {paymentMode === "selected" && (
                            <td>
                              <input
                                type="checkbox"
                                disabled={f.status === "Paid"}
                                checked={selectedFines.includes(f.fineId)}
                                onChange={() => toggleFineSelection(f.fineId)}
                              />
                            </td>
                          )}
                          <td>{f.fineType.toUpperCase().substring(0, 20)}</td>{" "}
                          <td>{formatDate(f.date)}</td>
                          <td>Rs {f.amount}</td>
                          <td>
                            <span
                              className={`status-badge ${
                                f.status === "Paid"
                                  ? "badge-paid"
                                  : "badge-pending"
                              }`}
                            >
                              {f.status === "Paid" ? "✔ PAID" : "⏳ PENDING"}
                            </span>
                          </td>
                          <td className="d-flex gap-2">
                            <button
                              className={`action-btn ${
                                f.status === "Paid" ? "btn-unpaid" : "btn-paid"
                              }`}
                              onClick={() =>
                                markFineStatus(
                                  f.userId,
                                  f.fineId,
                                  f.status === "Paid" ? "Pending" : "Paid",
                                )
                              }
                            >
                              {f.status === "Paid"
                                ? "↩ Mark Unpaid"
                                : "✔ Mark Paid"}
                            </button>
                            {/* 
                            <button
                              className="action-btn btn-delete"
                              onClick={() =>
                                handleDeleteFine(
                                  f.userId,
                                  f.fineId,
                                  f.amount,
                                  f.status,
                                  fetchData,
                                )
                              }
                            >
                              🗑 Delete
                            </button> */}

                            <button
                              className="action-btn btn-delete"
                              style={{
                                opacity: f.status === "Paid" ? 0.5 : 1,
                                cursor:
                                  f.status === "Paid"
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                              onClick={() => {
                                if (f.status === "Paid") {
                                  Swal.fire(
                                    "Cannot Delete",
                                    "This fine is Paid. First mark it as Unpaid, then delete.",
                                    "info",
                                  );
                                  return;
                                }
                                handleDeleteFine(
                                  f.userId,
                                  f.fineId,
                                  fetchData,
                                  true,
                                );
                              }}
                            >
                              🗑 Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          No fines found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              className="modal-btn modal-btn-cancel"
              onClick={() => {
                resetModal();
                setShow(false);
              }}
            >
              Cancel
            </button>

            <button
              className="modal-btn modal-btn-save"
              disabled={
                !selectedUser ||
                saving ||
                (paymentMode === "all"
                  ? totalPending === 0
                  : totalSelected === 0)
              }
              onClick={handleSavePayment}
            >
              {saving
                ? "Processing..."
                : `Save Payment (Rs ${
                    paymentMode === "all" ? totalPending : totalSelected
                  })`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaidUnpaidManager;
